import React, { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Typography,
  Divider,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  TagOutlined,
  PlayCircleOutlined,
  SoundOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useDynamicApi } from "../../hooks/useApi";
import { API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

const DidacticsPedagogyDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // API hooks
  const { execute: getContent, loading: contentLoading } = useDynamicApi("GET");

  // Category options for display
  const categoryOptions = [
    {
      value: "CREATING_TEAM_CULTURE",
      label: t("didacticsManager.categoryCreatingTeamCulture"),
    },
    {
      value: "UNMOTIVATED_PLAYERS",
      label: t("didacticsManager.categoryUnmotivatedPlayers"),
    },
    {
      value: "UNDESIRABLE_BEHAVIOR",
      label: t("didacticsManager.categoryUndesirableBehavior"),
    },
    {
      value: "DIFFERENCES_SKILL_LEVEL",
      label: t("didacticsManager.categoryDifferencesSkillLevel"),
    },
    {
      value: "MATCH_GUIDANCE_COACHING",
      label: t("didacticsManager.categoryMatchGuidanceCoaching"),
    },
  ];

  // Content type options for display
  const contentTypeOptions = [
    {
      value: "TIP",
      label: t("didacticsManager.typeTip"),
      icon: <FileTextOutlined />,
      color: "blue",
    },
    {
      value: "VIDEO",
      label: t("didacticsManager.typeVideo"),
      icon: <PlayCircleOutlined />,
      color: "green",
    },
    {
      value: "VOICE_OVER",
      label: t("didacticsManager.typeVoiceOver"),
      icon: <SoundOutlined />,
      color: "purple",
    },
  ];

  // Load content details
  const loadContentDetails = async () => {
    setLoading(true);
    try {
      const endpoint = `${API_ENDPOINTS.DIDACTICS_DETAIL_BY_ID}/${id}`;
      const response = await getContent(endpoint);

      if (response?.status === true && response.data) {
        setContent(response.data);
      } else {
        Toast.error(t("didacticsManager.failedFetchDetails"));
        navigate("/didactics");
      }
    } catch (error) {
      console.error("Error fetching content details:", error);
      Toast.error(error.message || t("didacticsManager.failedFetchDetails"));
      navigate("/didactics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadContentDetails();
    }
  }, [id]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get display name for category
  const getCategoryDisplay = (category) => {
    const found = categoryOptions.find((opt) => opt.value === category);
    return found ? found.label : category;
  };

  // Get content type info
  const getContentTypeInfo = (type) => {
    const found = contentTypeOptions.find((opt) => opt.value === type);
    return found || { label: type, icon: <TagOutlined />, color: "default" };
  };

  if (loading || contentLoading) return null;

  if (!content) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Title level={3}>{t("didacticsManager.noContentFound")}</Title>
          <Button type="primary" onClick={() => navigate("/didactics")}>
            {t("common.previous")} {t("didacticsManager.title")}
          </Button>
        </div>
      </Card>
    );
  }

  const contentTypeInfo = getContentTypeInfo(content.content_type);

  return (
    <div className="content-details-page">
      {/* Header with back button and actions */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/didactics")}
          >
            {t("common.previous")} {t("didacticsManager.title")}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t("didacticsManager.details")}
          </Title>
        </Space>
        {/* Edit button commented out like in other detail pages */}
        {/* <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/didactics/edit/${content.id}`)}
        >
          Edit Content
        </Button> */}
      </div>

      {/* Main Content */}
      <Card>
        {/* Avatar and Title Section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <div>
            <h1 style={{ margin: 0, marginBottom: "8px" }}>{content.title}</h1>
            <Space size="middle" wrap>
              <Tag color={contentTypeInfo.color}>{contentTypeInfo.label}</Tag>
              <Tag color={content.status === "ACTIVE" ? "green" : "default"}>
                {content.status === "ACTIVE"
                  ? t("common.active")
                  : t("common.inactive")}
              </Tag>
            </Space>
          </div>
        </div>

        {/* Content Details in Descriptions */}
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t("didacticsManager.title")} span={2}>
            <Space>
              <FileTextOutlined />
              {content.title}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("didacticsManager.category")} span={1}>
            {getCategoryDisplay(content.category)}
          </Descriptions.Item>

          <Descriptions.Item label={t("didacticsManager.type")} span={1}>
            <Tag color={contentTypeInfo.color}>{contentTypeInfo.label}</Tag>
          </Descriptions.Item>

          <Descriptions.Item
            label={t("didacticsManager.contentId") || "Content ID"}
            span={1}
          >
            {content.id}
          </Descriptions.Item>

          {content.duration && (
            <Descriptions.Item label={t("didacticsManager.duration")} span={1}>
              {content.duration}
            </Descriptions.Item>
          )}

          <Descriptions.Item label={t("common.createdAt")} span={1}>
            <Space>
              <CalendarOutlined />
              {formatDate(content.created_at)}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("common.lastUpdated")} span={1}>
            <Space>
              <ClockCircleOutlined />
              {formatDate(content.updated_at)}
            </Space>
          </Descriptions.Item>
        </Descriptions>

        {/* Content Type Specific Sections */}
        {content.content_type === "TIP" && content.tip_details && (
          <div style={{ marginTop: "24px" }}>
            <Divider orientation="left">
              {t("didacticsManager.tipDetails")}
            </Divider>
            <div
              style={{
                backgroundColor: "#f5f5f5",
                padding: "16px",
                borderRadius: "6px",
                whiteSpace: "pre-wrap",
                fontSize: "14px",
                lineHeight: "1.6",
              }}
            >
              {content.tip_details}
            </div>
          </div>
        )}

        {(content.content_type === "VIDEO" ||
          content.content_type === "VOICE_OVER") && (
          <>
            {content.short_description && (
              <div style={{ marginTop: "24px" }}>
                <Divider orientation="left">
                  {t("didacticsManager.description")}
                </Divider>
                <div
                  style={{
                    backgroundColor: "#f5f5f5",
                    padding: "16px",
                    borderRadius: "6px",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {content.short_description}
                </div>
              </div>
            )}

            {content.content_type === "VIDEO" && content.video_url && (
              <div style={{ marginTop: "24px" }}>
                <Divider orientation="left">
                  {t("didacticsManager.videoPreview") || "Video Preview"}
                </Divider>
                <div
                  style={{
                    maxWidth: "100%",
                    backgroundColor: "#000",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <video
                    controls
                    style={{ width: "100%", maxHeight: "400px" }}
                    poster={content.thumbnail_url}
                  >
                    <source src={content.video_url} type="video/mp4" />
                    {t("didacticsManager.videoNotSupported") ||
                      "Your browser does not support the video tag."}
                  </video>
                </div>
              </div>
            )}

            {content.content_type === "VOICE_OVER" && content.voice_url && (
              <div style={{ marginTop: "24px" }}>
                <Divider orientation="left">
                  {t("didacticsManager.audioPreview") || "Audio Preview"}
                </Divider>
                <div
                  style={{
                    backgroundColor: "#f5f5f5",
                    padding: "20px",
                    borderRadius: "6px",
                  }}
                >
                  <audio controls style={{ width: "100%" }}>
                    <source src={content.voice_url} type="audio/mpeg" />
                    {t("didacticsManager.audioNotSupported") ||
                      "Your browser does not support the audio element."}
                  </audio>
                </div>
              </div>
            )}

            {content.thumbnail_url && content.content_type === "VIDEO" && (
              <div style={{ marginTop: "24px" }}>
                <Divider orientation="left">
                  {t("didacticsManager.thumbnail")}
                </Divider>
                <img
                  src={content.thumbnail_url}
                  alt={t("didacticsManager.thumbnail")}
                  style={{
                    maxWidth: "300px",
                    maxHeight: "200px",
                    borderRadius: "6px",
                    border: "1px solid #f0f0f0",
                  }}
                />
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default DidacticsPedagogyDetails;
